/**
 * Custom chat button
 *
 * A customer draws their own launcher by putting an element with a reserved id
 * on the page; its presence is the entire opt-in. We write state classes onto it
 * and nothing else — never creating, styling, positioning or click-wiring it.
 *
 * The id and class names are unchanged from V1 (web/src/app/dom_mutators/enums.js)
 * so existing customer markup keeps working.
 */

import { isLegacyUIEnabled, type LegacyUISource } from '@/domain/legacyUiConfig';

/** The customer's element. Matched by id, exactly as in V1. */
export const CUSTOM_BUTTON_ID = 'custom-gladly-chat-button';

/** `hasAuthenticated` is V1's name: it tracks whether a user record exists at
 * all (anonymous included), not a JWT sign-in. */
export const CUSTOM_BUTTON_CLASSES = {
  hasAuthenticated: 'gladly-has-authenticated',
  show: 'gladly-show',
  unread: 'gladly-unread',
} as const;

export interface CustomButtonState {
  hasAuthenticated: boolean;
  hasUnread: boolean;
  show: boolean;
}

/** Whether the customer's launcher governs this page. The Legacy UI gate lives
 * here and nowhere else. */
export function isCustomButtonActive(server: LegacyUISource): boolean {
  return isLegacyUIEnabled(server) && isCustomButtonPresent();
}

export function isCustomButtonPresent(): boolean {
  return getCustomButton() !== null;
}

export function getCustomButton(): HTMLElement | null {
  return document.getElementById(CUSTOM_BUTTON_ID);
}

/**
 * Write the current state onto the customer's element; a no-op when there isn't
 * one. `hasAuthenticated` is written only while showing, matching V1 — customer
 * CSS compounds it with `.gladly-show` (see custom-button.html).
 */
export function applyCustomButtonState({ hasAuthenticated, hasUnread, show }: CustomButtonState): void {
  const button = getCustomButton();
  if (!button) return;

  if (show) {
    button.classList.toggle(CUSTOM_BUTTON_CLASSES.hasAuthenticated, hasAuthenticated);
    button.classList.add(CUSTOM_BUTTON_CLASSES.show);
  } else {
    button.classList.remove(CUSTOM_BUTTON_CLASSES.show);
  }

  // Unconditional, unlike the rest: V1 kept this current even while the panel
  // was open, so a customer's own dot clears as soon as the chat is read.
  button.classList.toggle(CUSTOM_BUTTON_CLASSES.unread, hasUnread);
}

/**
 * Call `onChange` whenever the element changes identity — appearing, going away,
 * or swapped for a different node. Returns a cancel.
 *
 * V1 called getElementById on every dispatched action, so its verdict was never
 * stale in either direction; this replaces that. Unbounded for the same reason:
 * every failure it prevents is mid-session. The cost is one id lookup and a
 * reference compare per mutation batch, and only on Legacy UI pages.
 *
 * Observes `documentElement`, not `body`, which does not exist yet when the
 * loader runs from `<head>`.
 */
export function watchCustomButton(onChange: (button: HTMLElement | null) => void): () => void {
  if (typeof MutationObserver === 'undefined') {
    return () => {};
  }

  let current = getCustomButton();
  const observer = new MutationObserver(() => {
    const next = getCustomButton();
    if (next === current) return;
    current = next;
    onChange(next);
  });

  // attributeFilter catches an element already in the DOM that gains the id,
  // which produces no childList record at all.
  observer.observe(document.documentElement, { attributeFilter: ['id'], childList: true, subtree: true });
  return () => observer.disconnect();
}

/** Take the launcher off screen, leaving its other state alone — what V1's
 * hideCustomMinimizedButton did. */
export function hideCustomButton(): void {
  getCustomButton()?.classList.remove(CUSTOM_BUTTON_CLASSES.show);
}
